(() => {
  if (window.DRBOFirstStepRecommendations) return;

  const money = (root, cents) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
        style: 'currency',
        currency: root.dataset.currency || 'EUR'
      }).format(cents / 100);
    } catch (error) {
      return `${(cents / 100).toFixed(2)} ${root.dataset.currency || 'EUR'}`;
    }
  };

  const price = (value) => {
    if (typeof value === 'string' && /[.,]/.test(value)) {
      return Math.round(Number(value.replace(',', '.')) * 100);
    }
    return Number(value || 0);
  };

  const imageUrl = (product) => {
    const image = product.featured_image || product.images?.[0] || '';
    return typeof image === 'string' ? image : image?.src || '';
  };

  const createCard = (root, group, product, choiceIndex) => {
    const variant = (product.variants || []).find((item) => item.available) || product.variants?.[0];
    const source = group.querySelector('[data-config-item][data-config-choice-index="1"]');
    if (!variant?.id || !(source instanceof HTMLElement)) return null;

    const itemPrice = price(variant.price);
    const comparePrice = price(variant.compare_at_price || variant.price);
    const itemImage = imageUrl(product);
    const card = source.cloneNode(true);
    card.classList.remove('is-selected', 'is-disabled');
    card.removeAttribute('data-config-first-step-fallback');
    Object.assign(card.dataset, {
      configFirstStepRecommendation: '',
      configChoiceIndex: String(choiceIndex),
      variantId: String(variant.id),
      productId: String(product.id || ''),
      collectionIds: String(root.dataset.firstStepEligibleCollectionId || ''),
      price: String(itemPrice),
      actualPrice: String(itemPrice),
      comparePrice: String(comparePrice),
      fixedQuantity: '1',
      activeQuantity: '1',
      itemTitle: product.title || '',
      itemUrl: product.url || '',
      itemImage,
      itemBadge: '',
      visualMode: 'main',
      visualImage: itemImage,
      available: String(variant.available !== false),
      switchImage: itemImage,
      switchFit: 'contain',
      switchPositionDesktop: 'center center',
      switchPositionMobile: 'center center',
      manualPriceDifference: ''
    });
    if (variant.available === false) card.classList.add('is-disabled');

    const input = card.querySelector(':scope > input');
    if (input) {
      input.value = String(variant.id);
      input.removeAttribute('checked');
      input.checked = false;
      input.disabled = variant.available === false;
      card.addEventListener('click', (event) => {
        if (event.target.closest('a, button, [data-config-stepper]')) return;
        event.preventDefault();
        event.stopPropagation();
        if (input.disabled || input.checked) return;
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }, true);
    }
    const title = card.querySelector('.drbo-configurator__product-title');
    if (title) title.textContent = product.title || '';
    card.querySelector('.drbo-configurator__badge')?.remove();

    const media = card.querySelector('.drbo-configurator__media');
    const image = media?.querySelector('img');
    if (itemImage && image) {
      image.src = itemImage;
      image.removeAttribute('srcset');
      image.alt = product.title || '';
    } else if (!itemImage) {
      media?.remove();
    }

    const priceNode = card.querySelector('.drbo-configurator__price');
    if (priceNode) priceNode.textContent = money(root, itemPrice);
    const compareNode = card.querySelector('.drbo-configurator__compare');
    if (compareNode) {
      compareNode.textContent = money(root, comparePrice);
      compareNode.hidden = comparePrice <= itemPrice;
    }
    const savingNode = card.querySelector('.drbo-configurator__saving');
    if (savingNode) {
      const saving = Math.max(0, comparePrice - itemPrice);
      savingNode.textContent = `${root.dataset.savingLabel || (document.documentElement.lang.split('-')[0] === 'en' ? 'You save' : 'Du sparst')} ${money(root, saving)}`;
      savingNode.hidden = saving <= 0;
    }
    const description = card.querySelector('.drbo-configurator__description');
    if (description) {
      description.textContent = String(product.description || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 220);
      description.hidden = !description.textContent;
    }
    return card;
  };

  const init = async (root) => {
    if (!(root instanceof HTMLElement) || root.dataset.firstStepSource !== 'current_shopify') return;
    const group = root.querySelector('[data-config-first-step]');
    const list = group?.querySelector('.drbo-configurator__items');
    if (!group || !list || group.dataset.autoRecommendationsLoaded === 'true') return;
    group.dataset.autoRecommendationsLoaded = 'true';
    const fallbacks = Array.from(group.querySelectorAll('[data-config-first-step-fallback]'));

    try {
      const params = new URLSearchParams({
        product_id: root.dataset.productId,
        limit: '10',
        intent: root.dataset.firstStepIntent || 'related'
      });
      const route = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${route}recommendations/products.json?${params}`, {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('recommendations');

      const payload = await response.json();
      const eligible = new Set(
        String(root.dataset.firstStepEligibleProducts || '').split('|').filter(Boolean).map(Number)
      );
      const currentId = Number(root.dataset.productId || 0);
      const currentType = String(root.dataset.productType || '').trim().toLowerCase();
      const matchType = root.dataset.firstStepMatchType === 'true' && currentType !== '';
      const limit = Math.max(1, Math.min(3, Number(root.dataset.firstStepLimit || 3)));
      const products = (Array.isArray(payload.products) ? payload.products : [])
        .filter((product) => {
          if (!product?.id || Number(product.id) === currentId) return false;
          if (eligible.size && !eligible.has(Number(product.id))) return false;
          const type = String(product.type || product.product_type || '').trim().toLowerCase();
          return !matchType || type === currentType;
        })
        .slice(0, limit);
      const cards = products.map((product, index) => createCard(root, group, product, index + 2)).filter(Boolean);
      if (!cards.length) throw new Error('empty');

      fallbacks.forEach((card) => card.remove());
      cards.forEach((card) => list.appendChild(card));
      root._drboRefreshPricing?.();
    } catch (error) {
      if (root.dataset.firstStepManualFallback !== 'true') {
        fallbacks.forEach((card) => card.remove());
        root._drboRefreshPricing?.();
      }
    }
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-drbo-configurator]').forEach(init);
  };

  window.DRBOFirstStepRecommendations = { initAll };
  initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
